import UploadFile from "@mui/icons-material/UploadFile";
import { Button, ButtonProps } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useState } from "react";

import { BillOfLadingReviewDialog } from "./BillOfLadingReviewDialog";
import { BillOfLadingUploadDialog } from "./BillOfLadingUploadDialog";

type AddBillOfLadingButtonProps = {
  tenantId: string;
  facilityId?: string;
  /** MUI Button size — defaults to "medium" */
  size?: ButtonProps["size"];
  /**
   * Called with the saved `WasteShippingDocument` id once the review is
   * applied, so a list/detail host can refetch and navigate. The button
   * still closes its own dialogs regardless of whether this is given.
   */
  onSaved?: (savedId: string) => void;
  /**
   * Opens the upload dialog from outside. Omit this and the component owns
   * its dialog state and renders its own "Add Bill of Lading" button, as it
   * always has. Pass it and the component renders the dialogs alone — the
   * caller supplies the trigger and holds the open state, which lets a host
   * with its own control (the merged list's split button) drive this flow
   * without reimplementing it. The review step that follows an upload stays
   * internal either way. Mirrors `AddManifestButton`.
   */
  open?: boolean;
  /** Called with the upload dialog's new open state. */
  onOpenChange?: (open: boolean) => void;
};

type ReviewTarget = { documentIds: string[]; facilityId: string };

/**
 * Owns the upload + review dialogs, mirroring `AddManifestButton`.
 *
 * Gated on the resolved `ContainerTracking` flag alone — the same gate the
 * sibling container-tracking surfaces use for their own entry points
 * (`WasteFacility.tsx`, `WasteContainerDetail.tsx`, `GeneratorStatusTile.tsx`
 * all read `featureFlags[FeatureFlagKey.ContainerTracking]` this way, and
 * `customerNavSectionsV2.ts` gates the Containers/Shipments nav items on it
 * too). Unlike `AddManifestButton`'s `useQueueManifestDocumentExtraction`,
 * there is no separate *stored* `WasteModule` check to add: the resolvers
 * (`extractBillOfLading`, `applyExtractedBillOfLading`) already gate on
 * `tenantHasFlag(ContainerTracking)` alone, with no backend pipeline reading
 * a different, unresolved flag the way manifest extraction does.
 */
export function AddBillOfLadingButton({
  tenantId,
  facilityId,
  size,
  onSaved,
  open: controlledOpen,
  onOpenChange,
}: AddBillOfLadingButtonProps) {
  const { featureFlags } = useFeatureFlags();
  const [ownOpen, setOwnOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);

  if (!featureFlags[FeatureFlagKey.ContainerTracking]) return null;

  const controlled = controlledOpen !== undefined;
  const open = controlledOpen ?? ownOpen;
  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };

  return (
    <>
      {!controlled && (
        <Button
          variant="contained"
          size={size}
          startIcon={<UploadFile />}
          onClick={() => setOpen(true)}
        >
          Add Bill of Lading
        </Button>
      )}

      {open && (
        <BillOfLadingUploadDialog
          open
          onClose={() => setOpen(false)}
          tenantId={tenantId}
          facilityId={facilityId}
          onExtractionReady={(documentIds, appliedFacilityId) =>
            setReviewTarget({ documentIds, facilityId: appliedFacilityId })
          }
        />
      )}

      {reviewTarget && (
        <BillOfLadingReviewDialog
          documentIds={reviewTarget.documentIds}
          facilityId={reviewTarget.facilityId}
          onClose={() => setReviewTarget(null)}
          onSaved={(savedId) => {
            setReviewTarget(null);
            onSaved?.(savedId);
          }}
        />
      )}
    </>
  );
}
